import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/lib/auth";
import api, { humanError, twoFactorRequired } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // Shown only after the password is accepted and a second factor is asked for.
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      // If this address belongs to a workspace using single sign-on, the
      // password field is not the way in -- send them to their provider
      // instead of letting them fail against a password they never set.
      if (!needsCode) {
        try {
          const { data: sso } = await api.post(`/sso/discover?email=${encodeURIComponent(email)}`);
          if (sso?.sso && sso.authorize_url) {
            window.location.href = sso.authorize_url;
            return;
          }
        } catch {
          // Discovery is a convenience; a failure here must not block sign-in.
        }
      }
      const data = await login(email, password, needsCode ? code : undefined);
      const dest = data.user.is_super_admin ? "/admin" : (loc.state?.from || "/app");
      toast.success(`Welcome back, ${data.user.name}`);
      nav(dest, { replace: true });
    } catch (err) {
      if (twoFactorRequired(err)) {
        // The password was right; only the second factor is outstanding.
        setNeedsCode(true);
        setCode("");
        toast.info("Enter the 6-digit code from your authenticator app");
      } else {
        if (needsCode) setCode("");
        toast.error(humanError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="hs-mint-gradient flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-[#E4E9EF] bg-white p-8 hs-card-shadow">
          <h1 className="font-display text-2xl font-bold text-[#0B2B33]">Sign in</h1>
          <p className="mt-1 text-sm text-[#43535E]">Welcome back. Enter your details below.</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]" htmlFor="email">Email</label>
              <input
                id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2.5 text-sm text-[#0B2B33] focus:border-[#1F5FD8] focus:outline-none focus:ring-2 focus:ring-[#1F5FD8]/20"
                placeholder="you@company.com" autoComplete="email"
              />
            </div>
            <div>
              <div className="mb-1 flex items-baseline justify-between">
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]" htmlFor="pw">Password</label>
                <Link to="/forgot-password" data-testid="login-forgot-link"
                  className="text-xs font-semibold text-[#1F5FD8] hover:underline">Forgot?</Link>
              </div>
              <input
                id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2.5 text-sm text-[#0B2B33] focus:border-[#1F5FD8] focus:outline-none focus:ring-2 focus:ring-[#1F5FD8]/20"
                placeholder="••••••••" autoComplete="current-password"
              />
            </div>
            {needsCode && (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]" htmlFor="totp">
                  Authentication code
                </label>
                <input
                  id="totp" type="text" required autoFocus value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9a-fA-F-]/g, ""))}
                  data-testid="login-totp-input"
                  className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2.5 text-center text-lg tracking-[0.4em] text-[#0B2B33] focus:border-[#1F5FD8] focus:outline-none focus:ring-2 focus:ring-[#1F5FD8]/20"
                  placeholder="000000" inputMode="numeric" autoComplete="one-time-code" maxLength={14}
                />
                <p className="mt-1.5 text-xs text-[#8A97A0]">
                  From your authenticator app, or use one of your recovery codes.
                </p>
              </div>
            )}
            <button type="submit" disabled={busy} data-testid="login-submit-button"
              className="hs-btn-primary w-full disabled:opacity-60">
              {busy ? "Signing in…" : needsCode ? "Verify code" : "Sign in"}
            </button>
          </form>
          <p className="mt-5 text-center text-sm text-[#43535E]">
            No account yet? <Link to="/signup" className="font-semibold text-[#1F5FD8] hover:underline" data-testid="login-signup-link">Create one</Link>
          </p>
        </div>
        {process.env.REACT_APP_SHOW_DEMO_ACCOUNTS === "true" && (
          <div className="mt-6 rounded-xl border border-[#E4E9EF] bg-white/70 p-4 text-xs text-[#43535E] backdrop-blur">
            <div className="mb-1 font-semibold text-[#0B2B33]">Demo accounts</div>
            <div>Admin — ada@northwind.io · Admin!234</div>
            <div>Editor — evan@northwind.io · Editor!234</div>
            <div>Approver — alice@northwind.io · Approver!234</div>
            <div>Super Admin — super@socialmind.in · SuperAdmin!234</div>
          </div>
        )}
      </div>
    </div>
  );
}
